import { el, fmt } from '../../lib/dom.js';
import { summen } from '../../lib/state.js';
import { fieldNum, fieldTextarea } from './widgets.js';
import type { WizardCtx } from './types.js';

export function renderErgebnis(ctx: WizardCtx, container: HTMLElement): void {
  const d = ctx.p.data;
  const head = el('div', { class: 'step-head' });
  head.append(
    el('div', { class: 'eyebrow' }, 'Schritt 6 von 7'),
    el('h2', {}, 'Ergebnis der Wertermittlung'),
    el(
      'p',
      {},
      'Nicht bewertete Gegenstände, Abzüge und Auflagen – daraus ergibt sich die Entschädigung insgesamt.',
    ),
  );
  container.append(head);

  // Nicht bewertete Anlagen
  const nbKarte = el('div', { class: 'card' });
  const nbTitel = el('h3', {}, 'Nicht bewertete Anlagen ');
  nbTitel.append(el('span', { class: 'sub' }, 'mit Begründung'));
  nbKarte.append(nbTitel);
  const nbListe = el('div');
  d.nb.forEach((r, i) => {
    const row = el('div', { class: 'linerow nb' });
    const a = el('input', { type: 'text', placeholder: 'Gegenstand, z. B. Geräteschuppen' });
    a.value = r.text;
    a.addEventListener('input', () => {
      r.text = a.value;
      ctx.save();
    });
    const b = el('input', { type: 'text', placeholder: 'Begründung, z. B. baufällig' });
    b.value = r.grund;
    b.addEventListener('input', () => {
      r.grund = b.value;
      ctx.save();
    });
    const x = el(
      'button',
      { class: 'btn-x', type: 'button', 'aria-label': 'Eintrag entfernen' },
      '✕',
    );
    x.addEventListener('click', () => {
      d.nb.splice(i, 1);
      ctx.save();
      ctx.rerender();
    });
    row.append(a, b, x);
    nbListe.append(row);
  });
  const nbAdd = el('button', { class: 'btn btn-add', type: 'button' }, '+ Eintrag hinzufügen');
  nbAdd.addEventListener('click', () => {
    d.nb.push({ text: '', grund: '' });
    ctx.save();
    ctx.rerender();
  });
  nbKarte.append(nbListe, nbAdd);

  // Abzüge
  const abzKarte = el('div', { class: 'card' });
  abzKarte.append(el('h3', {}, 'Abzüge für Beseitigung / Entsorgung'));
  const abzListe = el('div');
  d.abz.forEach((r, i) => {
    const row = el('div', { class: 'linerow' });
    const a = el('input', { type: 'text', placeholder: 'z. B. Grünabfall entsorgen' });
    a.value = r.text;
    a.addEventListener('input', () => {
      r.text = a.value;
      ctx.save();
    });
    const b = el('input', { type: 'number', inputmode: 'decimal', min: '0', placeholder: '€' });
    if (r.betrag) b.value = String(r.betrag);
    b.addEventListener('input', () => {
      r.betrag = Number(b.value) || 0;
      ctx.save();
      ctx.recalc();
      updateSummen();
    });
    const x = el(
      'button',
      { class: 'btn-x', type: 'button', 'aria-label': 'Abzug entfernen' },
      '✕',
    );
    x.addEventListener('click', () => {
      d.abz.splice(i, 1);
      ctx.save();
      ctx.recalc();
      ctx.rerender();
    });
    row.append(a, b, x);
    abzListe.append(row);
  });
  const abzAdd = el('button', { class: 'btn btn-add', type: 'button' }, '+ Abzug hinzufügen');
  abzAdd.addEventListener('click', () => {
    d.abz.push({ text: '', betrag: 0 });
    ctx.save();
    ctx.rerender();
  });
  abzKarte.append(abzListe, abzAdd);

  // Gemeinschaft + Auflagen
  const gemKarte = el('div', { class: 'card' });
  const gemTitel = el('h3', {}, 'Gemeinschaftseinrichtungen ');
  gemTitel.append(el('span', { class: 'sub' }, 'Leistungen des Pächters, werden hinzugerechnet'));
  gemKarte.append(
    gemTitel,
    fieldNum(
      'Betrag',
      () => d.gemeinschaft || null,
      (v) => (d.gemeinschaft = v ?? 0),
      () => {
        ctx.save();
        ctx.recalc();
        updateSummen();
      },
      { unit: '€' },
    ),
  );

  const auflKarte = el('div', { class: 'card' });
  auflKarte.append(
    el('h3', {}, 'Gesamtbewertung des Gartens / Auflagen'),
    fieldTextarea(
      'Freitext',
      () => d.auflagen,
      (v) => (d.auflagen = v),
      ctx.save,
      {
        placeholder:
          'z. B. Der Garten hat wenig kleingärtnerische Nutzung, es bedarf einiger Schnittarbeiten.',
      },
    ),
  );

  // Zusammenstellung
  const sumKarte = el('div', { class: 'card' });
  sumKarte.append(el('h3', {}, 'Zusammenstellung'));
  const zeilen: [string, (s: ReturnType<typeof summen>) => string, string?][] = [
    ['Baulichkeiten (Tabelle 1)', (s) => fmt(s.bau)],
    ['Nebenanlagen (Tabelle 2)', (s) => fmt(s.neben)],
    ['Gärtnerische Kulturen (Tabelle 3)', (s) => fmt(s.kulturen)],
    ['Zierbegrünung (Tabelle 4)', (s) => fmt(s.zier)],
    ['Leistungen für Gemeinschaftseinrichtungen', (s) => fmt(s.gemeinschaft)],
    ['Abzug für Beseitigung von Anpflanzungen und Anlagen', (s) => `−${fmt(s.abzuege)}`, 'neg'],
    ['Entschädigung insgesamt', (s) => fmt(s.entschaedigung), 'total'],
  ];
  const sumEls = zeilen.map(([label, , cls]) => {
    const zeile = el('div', { class: 'sumline' + (cls ? ` ${cls}` : '') });
    zeile.append(el('span', {}, label), el('strong'));
    sumKarte.append(zeile);
    return zeile;
  });
  const updateSummen = () => {
    const s = summen(ctx.p);
    zeilen.forEach(([, wert], i) => {
      const strong = sumEls[i]?.querySelector('strong');
      if (strong) strong.textContent = wert(s);
    });
  };
  updateSummen();

  container.append(nbKarte, abzKarte, gemKarte, auflKarte, sumKarte);
  container.addEventListener('input', updateSummen);
}
